import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useExperimentContext, useFeatureFlag } from '@americanexpress/one-experiments';
import { getActiveLocaleSelector, getIsLoggedInSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { useReactlytics } from '@americanexpress/reactlytics';
import { Button } from '@americanexpress/dls-react';
import { IconSearch } from '@americanexpress/dls-icons';
import { useRouterLocation } from '@americanexpress/one-app-router';
import { onSubmitThunk } from '../../../thunks/onSubmitThunk';
import hotelsContext from '../../../context/hotelsContext';
import { getIsSearchSubmitting } from '../../../selectors/searchSelectors';
import { HOMEPAGE_REDESIGN_EXPERIMENT, MODULE_NAME } from '../../../constants/globalConstants';
import { hasAnalyticsRule, hasLxpSearchRule } from '../../../constants/featureRulesConstants';
import { getLocationId, getLocationName, getLocationQuery } from '../../../selectors/requestSelectors';
import { createInav } from '../../../utils/createINav';

const SearchButton = () => {
  const {
    state: { moduleWrapperRef },
    config: { moduleId, taxonomy, searchRedirectConfig },
  } = React.useContext(hotelsContext);
  const { formatMessage } = useIntl();
  const isSearchSubmitting = useSelector(getIsSearchSubmitting);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const tenancy = useSelector(getTenancySelector);
  const locale = useSelector(getActiveLocaleSelector);
  const location = useRouterLocation();
  const isLxpSearch = useConditionalFeature(hasLxpSearchRule);

  const { enabled: forcedLoginFFEnabled, properties: forcedLoginFFProperties } = useFeatureFlag({ featureFlagName: 'prebooking_forced_login_HOTELS' });
  const forcedLoginLocales = forcedLoginFFProperties?.[tenancy]?.toLowerCase() || '';
  const isForcedLoginFeatureFlagEnabled = forcedLoginFFEnabled
    && forcedLoginLocales.includes(locale.toLowerCase());

  const {
    properties: cardModalProperties,
    enabled: cardModalEnabled,
  } = useFeatureFlag({ featureFlagName: 'prebooking_horizons_card_selection_modal' });

  const isCardModalEnabled = cardModalEnabled && cardModalProperties?.[tenancy]?.includes(locale);

  const horizonsConfig = {
    includeCenturion: true,
    isForcedLoginFeatureFlagEnabled,
    isCardModalEnabled,
    isLxpSearch,
  };

  const { selectors: { getExperimentVariant } } = useExperimentContext();
  const experimentData = getExperimentVariant(HOMEPAGE_REDESIGN_EXPERIMENT);
  const isVariantRedesign = useMemo(() => experimentData
    && experimentData.dimension === 'search_widget'
    && experimentData.variant !== 'control', [experimentData?.dimension, experimentData?.variant]);

  const locationId = useSelector(getLocationId);
  const locationQuery = useSelector(getLocationQuery);
  const locationName = useSelector(getLocationName);
  const hasAnalyticsEnabled = useConditionalFeature(hasAnalyticsRule);
  const { track } = useReactlytics();

  const dispatch = useDispatch();

  const triggerAnalytics = () => {
    const trackObj = {
      event: {
        element: 'searchHotelsSubmit',
        identifier: MODULE_NAME,
        type: 'click',
        ...hasAnalyticsEnabled
          ? {
            meta: {
              searchterm: `${locationQuery?.trim()}|${locationId?.trim().length > 0 ? locationId : locationName}`,
            },
          }
          : {},
      },
    };
    track(trackObj);
  };

  const bindProgramToSubmit = () => {
    createInav(location.pathname, dispatch);
    triggerAnalytics();
    dispatch(onSubmitThunk(taxonomy, searchRedirectConfig, horizonsConfig));

    if (!isVariantRedesign && global?.BROWSER && moduleWrapperRef?.current !== null) {
      // This isn't the best solution as header height varies between mobile and desktop but this
      // is as close as good until we figure out a better way to work out the header height
      const globalHeaderDomHeight = 110;
      const top = Number(moduleWrapperRef?.current?.getBoundingClientRect().top)
        + window.scrollY
        - globalHeaderDomHeight;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }
  };

  const btnAriaLabel = !isLoggedIn && isForcedLoginFeatureFlagEnabled
    ? 'ariaLabelForcedLogin'
    : 'ariaLabel';

  return (
    <Button
      aria-label={formatMessage({ id: `searchButton.${btnAriaLabel}` })}
      aria-describedby="search-hotels-sr-errors"
      onClick={isSearchSubmitting ? null : bindProgramToSubmit}
      isLoading={isSearchSubmitting}
      element="searchHotelsSubmit"
      id={`${moduleId}_searchButton`}
      className={isVariantRedesign ? 'fluid' : 'pad-0 fluid'}
      variant="primary"
    >
      {isVariantRedesign
        ? formatMessage({ id: 'search' })
        : (
          <>
            <IconSearch aria-hidden={true} className="hidden-sm-down" size="md" />
            <span className="hidden-md-up">{formatMessage({ id: 'search' })}</span>
          </>
        )}
    </Button>
  );
};

export default SearchButton;
